import { FC, ReactNode, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 200;
const DEFAULT_WIDTH_PX = 260;
// Above Metronic cards/tables so the popover is never clipped by an ancestor.
const Z_INDEX = 1080;
const GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

type Props = {
  text?: string;
  children?: ReactNode;
  widthPx?: number;
};

// Renders the popover with `position: fixed` anchored to the icon. Absolute
// positioning gets clipped by any ancestor with `overflow: hidden` (e.g. the
// funnel table card); fixed positioning escapes every overflow/stacking
// context so the explanation always floats on top.
const InfoTooltip: FC<Props> = ({
  text,
  children,
  widthPx = DEFAULT_WIDTH_PX,
}) => {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iconRef = useRef<HTMLElement>(null);

  function open() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Center under the icon, but keep both edges inside the viewport.
    const halfWidth = widthPx / 2;
    const minCenter = halfWidth + VIEWPORT_MARGIN_PX;
    const maxCenter = window.innerWidth - halfWidth - VIEWPORT_MARGIN_PX;
    const center = Math.min(
      Math.max(rect.left + rect.width / 2, minCenter),
      maxCenter
    );
    setCoords({ top: rect.bottom + GAP_PX, left: center });
  }

  function scheduleClose() {
    closeTimer.current = setTimeout(() => setCoords(null), CLOSE_DELAY_MS);
  }

  return (
    <span
      className="position-relative d-inline-block ms-2"
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
    >
      <i ref={iconRef} className="fas fa-info-circle text-muted" />
      {coords && (
        <span
          className="bg-white text-dark border rounded p-3 shadow fw-normal fs-7"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: 'translateX(-50%)',
            width: `${widthPx}px`,
            zIndex: Z_INDEX,
          }}
          onMouseEnter={open}
          onMouseLeave={scheduleClose}
        >
          {children ?? text}
        </span>
      )}
    </span>
  );
};

export default InfoTooltip;
